import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  ChartBarIcon, ArrowDownTrayIcon, ArrowPathIcon,
  XMarkIcon, EyeIcon, DocumentChartBarIcon,
} from '@heroicons/react/24/outline';

// ─── SWAL ─────────────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'rep-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('rep-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'rep-swal-z-style';
  s.textContent = '.rep-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PRIMARY    = '#800020';
const DARK       = '#1a1a2e';
const MONTHS     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtNum  = (n) => (n ?? 0).toLocaleString();
const fmtDate = (ts) => ts
  ? new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

// ─── UTC MIDNIGHT HELPERS (timezone-safe) ─────────────────────────────────────
//
// WHY UTC?
// Firebase stores all timestamps as UTC milliseconds since epoch.
// If we use local midnight (new Date(); d.setHours(0,0,0,0)) in EAT (UTC+3):
//   Local midnight March 22 EAT = 21:00 UTC March 21
//   So a record stored at e.g. 23:00 EAT on March 21 = 20:00 UTC March 21
//   falls AFTER 21:00 UTC March 21 → wrongly counted as "today".
//
// Fix: compute boundaries in UTC so they match how Firebase stored the timestamps.
//   UTC midnight March 22 = 00:00 UTC March 22 = correct boundary.

const startOfDayUTC = () => {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const startOfWeekUTC = () => {
  const d    = new Date();
  const diff = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff);
};

const startOfMonthUTC = () => {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

const startOfYearUTC = () => {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), 0, 1);
};

const getPeriodRange = (period) => {
  const now = Date.now();
  if (period === 'today') return { from: startOfDayUTC(),   to: now };
  if (period === 'week')  return { from: startOfWeekUTC(),  to: now };
  if (period === 'month') return { from: startOfMonthUTC(), to: now };
  if (period === 'year')  return { from: startOfYearUTC(),  to: now };
  return { from: 0, to: now };
};

// Safe range check — also guards against missing/zero timestamps
const inRange = (ts, from, to) => !!ts && ts >= from && ts <= to;

// ─── UTC CHART BUCKET HELPERS ─────────────────────────────────────────────────
// Use UTC getters so chart buckets match the same UTC day/month as stored timestamps
const utcHour       = (ts) => new Date(ts).getUTCHours();
const utcDayOfWeek  = (ts) => (new Date(ts).getUTCDay() + 6) % 7; // Mon=0…Sun=6
const utcDayOfMonth = (ts) => new Date(ts).getUTCDate();            // 1–31
const utcMonth      = (ts) => new Date(ts).getUTCMonth();           // 0–11

// ─── EXCEL EXPORT ─────────────────────────────────────────────────────────────
const exportExcel = (data, filename = 'report') => {
  const toTSV = (rows) =>
    rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join('\t')).join('\n');
  const blob = new Blob(['\uFEFF' + toTSV(data)], { type: 'text/tab-separated-values;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${filename}.xls`; a.click();
  URL.revokeObjectURL(url);
};

// ─── MINI BAR CHART (pure SVG) ───────────────────────────────────────────────
const MiniBarChart = ({ data, color = PRIMARY, height = 80, showLabels = true }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const w   = 100 / data.length;
  return (
    <div className="w-full" style={{ height }}>
      <svg width="100%" height="100%" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
        {data.map((d, i) => {
          const barH = Math.max((d.value / max) * (height - 20), 0);
          const x    = i * w + w * 0.1;
          const barW = w * 0.8;
          const y    = height - barH - (showLabels ? 16 : 4);
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} fill={color} rx="2" opacity="0.85" />
              {showLabels && (
                <text x={x + barW / 2} y={height - 2} textAnchor="middle"
                  fontSize="5" fill="#9ca3af" fontFamily="system-ui">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ─── MINI LINE CHART (pure SVG) ──────────────────────────────────────────────
const MiniLineChart = ({ data, color = PRIMARY, height = 60 }) => {
  if (!data?.length || data.length < 2) return null;
  const max   = Math.max(...data.map(d => d.value), 1);
  const min   = Math.min(...data.map(d => d.value), 0);
  const range = max - min || 1;
  const pad   = 8;
  const W = 200, H = height;
  const pts = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((d.value - min) / range) * (H - pad * 2);
    return `${x},${y}`;
  });
  const area = `M ${pts[0]} ${pts.slice(1).map(p => `L ${p}`).join(' ')} L ${pad + (W - pad*2)},${H-pad} L ${pad},${H-pad} Z`;
  return (
    <div className="w-full" style={{ height }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id={`lg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#lg-${color.replace('#','')})`} />
        <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const [x, y] = pts[i].split(',').map(Number);
          return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
        })}
      </svg>
    </div>
  );
};

// ─── DONUT CHART (pure SVG) ──────────────────────────────────────────────────
const DonutChart = ({ segments, size = 120 }) => {
  if (!segments?.length) return null;
  const total       = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const r           = 40, cx = 60, cy = 60, strokeW = 18;
  const circ        = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={strokeW} />
      {segments.map((seg, i) => {
        const pct  = seg.value / total;
        const dash = pct * circ;
        const gap  = circ - dash;
        const el   = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={strokeW}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset * circ}
            strokeLinecap="round"
            style={{ transform:'rotate(-90deg)', transformOrigin:'60px 60px' }}
          />
        );
        offset += pct;
        return el;
      })}
      <text x={cx} y={cy-6}  textAnchor="middle" fontSize="14" fontWeight="700" fill={DARK}>{fmtNum(total)}</text>
      <text x={cx} y={cy+10} textAnchor="middle" fontSize="7"  fill="#9ca3af">TOTAL</text>
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL MODAL — full screen slide-in (same style as inventory/sale/orders)
// ═══════════════════════════════════════════════════════════════════════════════
const ReportDetailModal = ({ title, subtitle, data, columns, onClose, onExport, gradient }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 flex"
    style={{ zIndex: 9999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
    onClick={e => e.target === e.currentTarget && onClose()}>

    <motion.div
      initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 280 }}
      className="ml-auto flex flex-col bg-white shadow-2xl"
      style={{ width: '100%', height: '100vh' }}
      onClick={e => e.stopPropagation()}>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
        style={{ background: gradient || `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <p className="text-white/60 text-sm mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <button onClick={onExport}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: 'rgba(255,255,255,0.2)' }}>
              <ArrowDownTrayIcon className="h-4 w-4" /> Export Excel
            </button>
          )}
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-8 py-6" style={{ minHeight: 0 }}>
        {data?.length > 0 && columns?.length > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                    {columns.map(col => (
                      <th key={col.key}
                        className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.map((row, i) => (
                    <motion.tr key={i}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.015 }}
                      className="hover:bg-gray-50 transition-colors">
                      {columns.map(col => (
                        <td key={col.key} className="px-4 py-3 text-sm text-gray-700">
                          {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                        </td>
                      ))}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <DocumentChartBarIcon className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-medium">No data for this period</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
        <p className="text-xs text-gray-400">{data?.length || 0} records</p>
        <button onClick={onClose}
          className="px-8 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
          Close
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// ─── REPORT CARD ──────────────────────────────────────────────────────────────
const ReportCard = ({ title, subtitle, count, revenue, icon, gradient, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="stat-card rounded-2xl p-5 cursor-pointer"
    style={{ background: gradient, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
    onClick={onClick}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-2xl">{icon}</span>
      <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold"
        style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
        <EyeIcon className="h-3 w-3" /> View
      </div>
    </div>
    <p className="text-white/60 text-xs mb-1">{subtitle}</p>
    <p className="text-white font-black text-xl leading-tight">KES {fmtNum(revenue)}</p>
    <p className="text-white/60 text-xs mt-2">{count} transactions</p>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN REPORTS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Reports = () => {
  const { userData } = useAuth();
  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAuthorized = userRole === 'admin' || userRole === 'superadmin';

  const [period,  setPeriod]  = useState('month');
  const [loading, setLoading] = useState(true);
  const [data,    setData]    = useState(null);
  const [modal,   setModal]   = useState(null);

  // ── Fetch all raw data ────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersSnap, salesSnap, usersSnap, guestSnap] = await Promise.all([
        get(ref(database, 'orders')),
        get(ref(database, 'sales')),
        get(ref(database, 'users')),
        get(ref(database, 'guestVisits')),
      ]);
      setData({
        orders:  ordersSnap.exists() ? Object.entries(ordersSnap.val()).map(([k,v]) => ({ id: k, ...v })) : [],
        sales:   salesSnap.exists()  ? Object.entries(salesSnap.val()).map(([k,v])  => ({ id: k, ...v })) : [],
        users:   usersSnap.exists()  ? Object.entries(usersSnap.val()).map(([k,v])  => ({ id: k, ...v })) : [],
        guests:  guestSnap.exists()  ? Object.entries(guestSnap.val()).map(([k,v])  => ({ id: k, ...v })) : [],
      });
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to load reports data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Computed stats — recalculates whenever period or data changes ─────────
  const stats = React.useMemo(() => {
    if (!data) return null;

    const { from, to }             = getPeriodRange(period);
    const { orders, sales, users, guests } = data;

    // ── ORDERS: collected + paid only ──────────────────────────────────────
    // Always use collectedAt as the completion timestamp.
    // Only fall back to createdAt when collectedAt is genuinely absent (legacy records).
    const completedOrders = orders.filter(o =>
      o.orderStatus   === 'collected' &&
      o.paymentStatus === 'paid'      &&
      inRange(o.collectedAt || o.createdAt, from, to)
    );
    const orderRevenue = completedOrders.reduce((s, o) => s + (o.total || 0), 0);

    // ── SALES: paid only ──────────────────────────────────────────────────
    const completedSales = sales.filter(s =>
      s.paymentStatus === 'paid' &&
      inRange(s.createdAt, from, to)
    );
    const saleRevenue = completedSales.reduce((s, sl) => s + (sl.total || 0), 0);

    // ── MEMBERSHIPS: completed paymentUIDs ───────────────────────────────
    const memberPayments = [];
    users.forEach(u => {
      Object.values(u.paymentUIDs || {}).forEach(uid => {
        if (
          uid.status === 'completed' &&
          inRange(uid.activationDate || uid.createdAt || 0, from, to)
        ) {
          memberPayments.push({ ...uid, userName: u.personalInfo?.name, userId: u.id });
        }
      });
    });
    const membershipRevenue = memberPayments.reduce((s, m) => s + (m.packagePrice || 0), 0);

    // ── GUEST VISITS: completed only ──────────────────────────────────────
    const completedGuests = guests.filter(g =>
      g.status === 'completed' &&
      inRange(g.checkedIn, from, to)
    );
    const guestRevenue = completedGuests.reduce((s, g) => s + (g.amountPaid || g.totalPrice || 0), 0);

    const totalRevenue = orderRevenue + saleRevenue + membershipRevenue + guestRevenue;

    // ── MEMBER COUNTS ──────────────────────────────────────────────────────
    const newMembers    = users.filter(u => inRange(u.personalInfo?.registeredAt || 0, from, to));
    const activeMembers = users.filter(u => u.membership?.status === 'active');

    // ── TOP PRODUCTS ──────────────────────────────────────────────────────
    const productMap = {};
    completedOrders.forEach(o =>
      (o.items || []).forEach(item => {
        const k = item.name;
        if (!productMap[k]) productMap[k] = { name: k, qty: 0, revenue: 0 };
        productMap[k].qty     += item.quantity || 1;
        productMap[k].revenue += (item.price || 0) * (item.quantity || 1);
      })
    );
    completedSales.forEach(s =>
      (s.items || []).forEach(item => {
        const k = item.name;
        if (!productMap[k]) productMap[k] = { name: k, qty: 0, revenue: 0 };
        productMap[k].qty     += item.quantity || 1;
        productMap[k].revenue += (item.unitPrice || 0) * (item.quantity || 1);
      })
    );
    const topProducts = Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 10);

    // ── TOP ACTIVITIES ────────────────────────────────────────────────────
    const actMap = {};
    completedGuests.forEach(g => {
      const t = g.activityTitle || 'Unknown';
      if (!actMap[t]) actMap[t] = { title: t, visits: 0, revenue: 0 };
      actMap[t].visits++;
      actMap[t].revenue += g.amountPaid || g.totalPrice || 0;
    });
    const topActivities = Object.values(actMap).sort((a, b) => b.revenue - a.revenue).slice(0, 10);

    // ── CHARTS ────────────────────────────────────────────────────────────
    // All bucket comparisons use UTC getters so they align with how
    // Firebase stored the timestamps (UTC milliseconds).
    const allRevItems = [
      ...completedOrders.map(o => ({ ts: o.collectedAt || o.createdAt, val: o.total || 0 })),
      ...completedSales.map(s  => ({ ts: s.createdAt,                  val: s.total || 0 })),
      ...memberPayments.map(m  => ({ ts: m.activationDate || 0,        val: m.packagePrice || 0 })),
      ...completedGuests.map(g => ({ ts: g.checkedIn,                  val: g.amountPaid || g.totalPrice || 0 })),
    ];
    const guestItems = completedGuests.map(g => ({ ts: g.checkedIn, val: 1 }));

    const buildChart = (items) => {
      if (period === 'today') {
        return Array.from({ length: 24 }, (_, i) => ({
          label: `${i}h`,
          value: items.filter(x => utcHour(x.ts) === i).reduce((s, x) => s + x.val, 0),
        }));
      }
      if (period === 'week') {
        return DAYS_SHORT.map((label, i) => ({
          label,
          value: items.filter(x => utcDayOfWeek(x.ts) === i).reduce((s, x) => s + x.val, 0),
        }));
      }
      if (period === 'month') {
        return Array.from({ length: 31 }, (_, i) => ({
          label: `${i + 1}`,
          value: items.filter(x => utcDayOfMonth(x.ts) === i + 1).reduce((s, x) => s + x.val, 0),
        }));
      }
      if (period === 'year') {
        return MONTHS.map((label, i) => ({
          label,
          value: items.filter(x => utcMonth(x.ts) === i).reduce((s, x) => s + x.val, 0),
        }));
      }
      return [];
    };

    const revenueChart = buildChart(allRevItems);
    const visitsChart  = buildChart(guestItems);

    // ── PAYMENT METHODS ───────────────────────────────────────────────────
    const methodMap = {};
    [...completedOrders, ...completedSales].forEach(item => {
      const method = item.paymentMethod || item.payments?.[0]?.method || 'unknown';
      methodMap[method] = (methodMap[method] || 0) + (item.total || 0);
    });
    const METHOD_COLORS = {
      cash: '#16a34a', mpesa: '#0ea5e9', wallet: PRIMARY,
      visa: '#2563eb', mastercard: '#dc2626', online: '#7c3aed',
      paystack: '#f59e0b', whatsapp_cod: '#25D366', unknown: '#9ca3af',
    };
    const payMethodSegments = Object.entries(methodMap).map(([method, value]) => ({
      label: method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
      color: METHOD_COLORS[method.toLowerCase()] || '#9ca3af',
    }));

    return {
      totalRevenue, orderRevenue, saleRevenue, membershipRevenue, guestRevenue,
      completedOrders, completedSales, memberPayments, completedGuests,
      newMembers, activeMembers,
      topProducts, topActivities,
      revenueChart, visitsChart, payMethodSegments,
    };
  }, [data, period]);

  // ── Period label ──────────────────────────────────────────────────────────
  const periodLabel = { today:'Today', week:'This Week', month:'This Month', year:'This Year' }[period];

  // ── Excel exports ─────────────────────────────────────────────────────────
  const exportOrders = () => {
    if (!stats?.completedOrders.length) return toast('warning', 'No data to export');
    exportExcel([
      ['Order #','Customer','Phone','Items','Total (KES)','Method','Collected Date'],
      ...stats.completedOrders.map(o => [
        o.orderNumber, o.userInfo?.name||'—', o.userInfo?.phone||'—',
        (o.items||[]).map(i=>`${i.name} x${i.quantity}`).join('; '),
        o.total, o.paymentMethod, fmtDate(o.collectedAt||o.createdAt),
      ]),
    ], `orders_${period}_${new Date().toISOString().slice(0,10)}`);
    toast('success','Orders exported');
  };

  const exportSales = () => {
    if (!stats?.completedSales.length) return toast('warning', 'No data to export');
    exportExcel([
      ['Sale #','Customer','Phone','Items','Total (KES)','Payment','Date'],
      ...stats.completedSales.map(s => [
        s.saleNumber, s.customerName||'—', s.customerPhone||'—',
        (s.items||[]).map(i=>`${i.name} x${i.quantity}`).join('; '),
        s.total, (s.payments||[]).map(p=>`${p.method} KES ${p.amount}`).join('; '),
        fmtDate(s.createdAt),
      ]),
    ], `sales_${period}_${new Date().toISOString().slice(0,10)}`);
    toast('success','Sales exported');
  };

  const exportMemberships = () => {
    if (!stats?.memberPayments.length) return toast('warning', 'No data to export');
    exportExcel([
      ['Member','Package','Plan','Amount (KES)','Activation Date','Expiry Date'],
      ...stats.memberPayments.map(m => [
        m.userName||'—', m.packageName||m.packageId||'—', m.planKey||'—',
        m.packagePrice||0, fmtDate(m.activationDate), fmtDate(m.expiryDate),
      ]),
    ], `memberships_${period}_${new Date().toISOString().slice(0,10)}`);
    toast('success','Memberships exported');
  };

  const exportGuestVisits = () => {
    if (!stats?.completedGuests.length) return toast('warning', 'No data to export');
    exportExcel([
      ['Guest','Phone','Activity','Check In','Duration (min)','Paid (KES)','Method'],
      ...stats.completedGuests.map(g => [
        g.guestName||'—', g.guestPhone||'—', g.activityTitle||'—',
        fmtDate(g.checkedIn), g.actualDurationMinutes||'—',
        g.amountPaid||g.totalPrice||0, g.paymentMethod||'—',
      ]),
    ], `guest_visits_${period}_${new Date().toISOString().slice(0,10)}`);
    toast('success','Guest visits exported');
  };

  const exportFullReport = () => {
    if (!stats) return;
    exportExcel([
      [`POWERCORE GYM — ${periodLabel.toUpperCase()} REPORT`,'','','','',''],
      [`Generated: ${new Date().toLocaleString('en-KE')}`,'','','','',''],
      [],
      ['REVENUE SUMMARY','','','','',''],
      ['Category','Amount (KES)','','','',''],
      ['Completed Orders',  stats.orderRevenue,      '','','',''],
      ['In-Store Sales',    stats.saleRevenue,       '','','',''],
      ['Memberships',       stats.membershipRevenue, '','','',''],
      ['Guest Visits',      stats.guestRevenue,      '','','',''],
      ['TOTAL REVENUE',     stats.totalRevenue,      '','','',''],
      [],
      ['MEMBER STATS','','','','',''],
      ['New Members',    stats.newMembers.length,    '','','',''],
      ['Active Members', stats.activeMembers.length, '','','',''],
      [],
      ['TOP PRODUCTS','','','','',''],
      ['Product','Units Sold','Revenue (KES)','','',''],
      ...stats.topProducts.map(p=>[p.name,p.qty,p.revenue,'','','']),
    ], `full_report_${period}_${new Date().toISOString().slice(0,10)}`);
    toast('success','Full report exported');
  };

  if (!isAuthorized) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background:'#f8f9fa' }}>
      <ChartBarIcon className="h-16 w-16 text-gray-200" />
      <p className="text-gray-500">Admin access required</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{
      background: 'linear-gradient(135deg,#0f0f1a 0%,#1a1a2e 40%,#2d0010 100%)',
      fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        .stat-card { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .stat-card:hover { transform: translateY(-4px); }
      `}</style>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* ── HEADER ── */}
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background:`linear-gradient(135deg,${PRIMARY},#c0002f)`, boxShadow:`0 8px 24px ${PRIMARY}50` }}>
              <ChartBarIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight">Reports</h1>
              <p className="text-white/40 text-sm">Completed transactions only · {periodLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll}
              className="w-9 h-9 flex items-center justify-center rounded-xl border text-white/60 hover:text-white transition-all"
              style={{ borderColor:'rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.05)' }}>
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={exportFullReport}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
              style={{ background:`linear-gradient(135deg,${PRIMARY},#c0002f)`, boxShadow:`0 4px 16px ${PRIMARY}40` }}>
              <ArrowDownTrayIcon className="h-4 w-4" /> Export Full Report
            </button>
          </div>
        </motion.div>

        {/* ── PERIOD TABS ── */}
        <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
          transition={{ delay:0.1 }} className="flex gap-2 mb-8 flex-wrap">
          {[
            { key:'today', label:'Today'      },
            { key:'week',  label:'This Week'  },
            { key:'month', label:'This Month' },
            { key:'year',  label:'This Year'  },
          ].map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
              style={{
                background: period === p.key ? `linear-gradient(135deg,${PRIMARY},#c0002f)` : 'rgba(255,255,255,0.07)',
                color:      period === p.key ? '#fff' : 'rgba(255,255,255,0.5)',
                boxShadow:  period === p.key ? `0 4px 16px ${PRIMARY}40` : 'none',
              }}>
              {p.label}
            </button>
          ))}
        </motion.div>

        {/* ── LOADING ── */}
        {loading ? (
          <div className="text-center py-32">
            <div className="inline-block w-10 h-10 border-4 border-red-900 border-t-red-400 rounded-full animate-spin" />
            <p className="text-white/40 mt-4">Loading report data…</p>
          </div>
        ) : stats && (
          <>
            {/* ── REVENUE CARDS ── */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {[
                { label:'Total Revenue',  val:stats.totalRevenue,      icon:'💰', gradient:'linear-gradient(135deg,#78350f,#92400e)' },
                { label:'Shop Orders',    val:stats.orderRevenue,      icon:'📦', gradient:'linear-gradient(135deg,#1e3a5f,#1d4ed8)' },
                { label:'In-Store Sales', val:stats.saleRevenue,       icon:'🛒', gradient:'linear-gradient(135deg,#064e3b,#059669)' },
                { label:'Memberships',    val:stats.membershipRevenue, icon:'🏋️', gradient:`linear-gradient(135deg,#4a0020,${PRIMARY})` },
                { label:'Guest Visits',   val:stats.guestRevenue,      icon:'🪪', gradient:'linear-gradient(135deg,#2e1065,#7c3aed)' },
              ].map((card, i) => (
                <motion.div key={i} className="stat-card rounded-2xl p-5"
                  initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:0.1 + i*0.06 }}
                  style={{ background:card.gradient, boxShadow:'0 8px 24px rgba(0,0,0,0.3)' }}>
                  <p className="text-2xl mb-3">{card.icon}</p>
                  <p className="text-white/60 text-xs font-medium mb-1">{card.label}</p>
                  <p className="text-white font-black text-xl leading-tight">KES {fmtNum(card.val)}</p>
                </motion.div>
              ))}
            </div>

            {/* ── REVENUE TREND ── */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.3 }}
              className="rounded-2xl p-6 mb-6"
              style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-bold">Revenue Trend</h3>
                  <p className="text-white/40 text-xs mt-0.5">All completed sources · {periodLabel} · UTC aligned</p>
                </div>
                <span className="text-xs text-white/40 font-mono">KES {fmtNum(stats.totalRevenue)}</span>
              </div>
              <MiniLineChart data={stats.revenueChart} color={PRIMARY} height={100} />
            </motion.div>

            {/* ── SECONDARY CHARTS ── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">

              {/* Guest visits bar */}
              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.35 }}
                className="rounded-2xl p-5"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-white font-bold text-sm mb-1">Guest Visit Frequency</h3>
                <p className="text-white/40 text-xs mb-4">{stats.completedGuests.length} visits · {periodLabel}</p>
                <MiniBarChart data={stats.visitsChart} color="#7c3aed" height={80} />
              </motion.div>

              {/* Payment method donut */}
              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.4 }}
                className="rounded-2xl p-5 flex flex-col"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-white font-bold text-sm mb-1">Payment Methods</h3>
                <p className="text-white/40 text-xs mb-4">Revenue by method</p>
                {stats.payMethodSegments.length > 0 ? (
                  <div className="flex items-center gap-4">
                    <DonutChart segments={stats.payMethodSegments} size={100} />
                    <div className="flex-1 space-y-1.5">
                      {stats.payMethodSegments.slice(0,5).map((s,i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background:s.color }} />
                          <span className="text-white/60 text-xs truncate flex-1">{s.label}</span>
                          <span className="text-white/80 text-xs font-bold">KES {fmtNum(s.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-white/30 text-sm">No payment data</p>
                  </div>
                )}
              </motion.div>

              {/* Member stats */}
              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.45 }}
                className="rounded-2xl p-5"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-white font-bold text-sm mb-4">Member Stats</h3>
                <div className="space-y-3">
                  {[
                    { label:'New Members',     val:stats.newMembers.length,      color:'#34d399', icon:'👥' },
                    { label:'Active Members',  val:stats.activeMembers.length,   color:'#60a5fa', icon:'✅' },
                    { label:'New Memberships', val:stats.memberPayments.length,  color:'#f472b6', icon:'🏋️' },
                    { label:'Guest Visits',    val:stats.completedGuests.length, color:'#a78bfa', icon:'🪪' },
                  ].map((s,i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-lg">{s.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white/60 text-xs">{s.label}</span>
                          <span className="text-white font-bold text-sm">{s.val}</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                            style={{ width:`${Math.min(100, s.val*10)}%`, background:s.color }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* ── DETAIL REPORT CARDS ── */}
            <h2 className="text-white font-bold text-lg mb-4">Detailed Reports</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <ReportCard title="Shop Orders"    subtitle="Completed & paid"               count={stats.completedOrders.length}  revenue={stats.orderRevenue}      icon="📦" gradient="linear-gradient(135deg,#1e3a5f,#1d4ed8)"          delay={0.5}  onClick={()=>setModal('orders')} />
              <ReportCard title="In-Store Sales" subtitle="Completed POS sales"            count={stats.completedSales.length}   revenue={stats.saleRevenue}       icon="🛒" gradient="linear-gradient(135deg,#064e3b,#059669)"          delay={0.55} onClick={()=>setModal('sales')} />
              <ReportCard title="Memberships"    subtitle="Completed payments"             count={stats.memberPayments.length}   revenue={stats.membershipRevenue} icon="🏋️" gradient={`linear-gradient(135deg,#4a0020,${PRIMARY})`}     delay={0.6}  onClick={()=>setModal('memberships')} />
              <ReportCard title="Guest Visits"   subtitle="Completed guest access"         count={stats.completedGuests.length}  revenue={stats.guestRevenue}      icon="🪪" gradient="linear-gradient(135deg,#2e1065,#7c3aed)"          delay={0.65} onClick={()=>setModal('guests')} />
            </div>

            {/* ── TOP PRODUCTS + ACTIVITIES ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.7 }}
                className="rounded-2xl overflow-hidden"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor:'rgba(255,255,255,0.08)' }}>
                  <div>
                    <h3 className="text-white font-bold">Top Products</h3>
                    <p className="text-white/40 text-xs mt-0.5">By revenue · {periodLabel}</p>
                  </div>
                  <button onClick={()=>setModal('top_products')}
                    className="flex items-center gap-1.5 text-xs font-bold" style={{ color:'#60a5fa' }}>
                    <EyeIcon className="h-4 w-4" /> View All
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {stats.topProducts.slice(0,5).length === 0 ? (
                    <p className="text-white/30 text-sm text-center py-4">No product sales this period</p>
                  ) : stats.topProducts.slice(0,5).map((p,i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                        style={{ background: i===0?'#fbbf24':i===1?'#9ca3af':i===2?'#cd7c2f':'rgba(255,255,255,0.1)', color: i<3?'#fff':'rgba(255,255,255,0.5)' }}>
                        {i+1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{p.name}</p>
                        <p className="text-white/40 text-xs">{p.qty} units sold</p>
                      </div>
                      <span className="font-bold text-sm flex-shrink-0" style={{ color:'#60a5fa' }}>
                        KES {fmtNum(p.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.75 }}
                className="rounded-2xl overflow-hidden"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor:'rgba(255,255,255,0.08)' }}>
                  <div>
                    <h3 className="text-white font-bold">Top Activities</h3>
                    <p className="text-white/40 text-xs mt-0.5">Guest visits by revenue · {periodLabel}</p>
                  </div>
                  <button onClick={()=>setModal('top_activities')}
                    className="flex items-center gap-1.5 text-xs font-bold" style={{ color:'#a78bfa' }}>
                    <EyeIcon className="h-4 w-4" /> View All
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {stats.topActivities.slice(0,5).length === 0 ? (
                    <p className="text-white/30 text-sm text-center py-4">No guest visits this period</p>
                  ) : stats.topActivities.slice(0,5).map((a,i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                        style={{ background: i===0?'#fbbf24':i===1?'#9ca3af':i===2?'#cd7c2f':'rgba(255,255,255,0.1)', color: i<3?'#fff':'rgba(255,255,255,0.5)' }}>
                        {i+1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{a.title}</p>
                        <p className="text-white/40 text-xs">{a.visits} visits</p>
                      </div>
                      <span className="font-bold text-sm flex-shrink-0" style={{ color:'#a78bfa' }}>
                        KES {fmtNum(a.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </div>

      {/* ── DETAIL MODALS ── */}
      <AnimatePresence>

        {modal === 'orders' && stats && (
          <ReportDetailModal
            title="Shop Orders Report"
            subtitle={`${stats.completedOrders.length} completed orders · ${periodLabel}`}
            gradient="linear-gradient(135deg,#1e3a5f,#1d4ed8)"
            data={stats.completedOrders.map(o => ({
              orderNumber: o.orderNumber,
              customer:    o.userInfo?.name  || '—',
              phone:       o.userInfo?.phone || '—',
              items:       (o.items||[]).map(i=>`${i.name} ×${i.quantity}`).join(', '),
              total:       o.total,
              method:      o.paymentMethod,
              date:        fmtDate(o.collectedAt||o.createdAt),
            }))}
            columns={[
              { key:'orderNumber', label:'Order #' },
              { key:'customer',    label:'Customer' },
              { key:'phone',       label:'Phone' },
              { key:'items',       label:'Items' },
              { key:'total',       label:'Total',  render: v=>`KES ${fmtNum(v)}` },
              { key:'method',      label:'Method' },
              { key:'date',        label:'Date' },
            ]}
            onClose={()=>setModal(null)}
            onExport={exportOrders}
          />
        )}

        {modal === 'sales' && stats && (
          <ReportDetailModal
            title="In-Store Sales Report"
            subtitle={`${stats.completedSales.length} completed sales · ${periodLabel}`}
            gradient="linear-gradient(135deg,#064e3b,#059669)"
            data={stats.completedSales.map(s => ({
              saleNumber: s.saleNumber,
              customer:   s.customerName  || '—',
              phone:      s.customerPhone || '—',
              items:      (s.items||[]).map(i=>`${i.name} ×${i.quantity}`).join(', '),
              total:      s.total,
              payments:   (s.payments||[]).map(p=>`${p.method} KES ${p.amount}`).join('; '),
              date:       fmtDate(s.createdAt),
            }))}
            columns={[
              { key:'saleNumber', label:'Sale #' },
              { key:'customer',   label:'Customer' },
              { key:'items',      label:'Items' },
              { key:'total',      label:'Total',    render: v=>`KES ${fmtNum(v)}` },
              { key:'payments',   label:'Payments' },
              { key:'date',       label:'Date' },
            ]}
            onClose={()=>setModal(null)}
            onExport={exportSales}
          />
        )}

        {modal === 'memberships' && stats && (
          <ReportDetailModal
            title="Membership Payments Report"
            subtitle={`${stats.memberPayments.length} completed memberships · ${periodLabel}`}
            gradient={`linear-gradient(135deg,#4a0020,${PRIMARY})`}
            data={stats.memberPayments.map(m => ({
              member:    m.userName || '—',
              package:   m.packageName||m.packageId||'—',
              plan:      m.planKey||'—',
              amount:    m.packagePrice||0,
              activated: fmtDate(m.activationDate),
              expires:   fmtDate(m.expiryDate),
            }))}
            columns={[
              { key:'member',    label:'Member' },
              { key:'package',   label:'Package' },
              { key:'plan',      label:'Plan' },
              { key:'amount',    label:'Amount',    render: v=>`KES ${fmtNum(v)}` },
              { key:'activated', label:'Activated' },
              { key:'expires',   label:'Expires' },
            ]}
            onClose={()=>setModal(null)}
            onExport={exportMemberships}
          />
        )}

        {modal === 'guests' && stats && (
          <ReportDetailModal
            title="Guest Visits Report"
            subtitle={`${stats.completedGuests.length} completed guest visits · ${periodLabel}`}
            gradient="linear-gradient(135deg,#2e1065,#7c3aed)"
            data={stats.completedGuests.map(g => ({
              guest:    g.guestName||'—',
              phone:    g.guestPhone||'—',
              activity: g.activityTitle||'—',
              checkIn:  fmtDate(g.checkedIn),
              duration: g.actualDurationMinutes ? `${g.actualDurationMinutes}min` : '—',
              paid:     g.amountPaid||g.totalPrice||0,
              method:   g.paymentMethod||'—',
              type:     g.isRegisteredMember ? 'Member' : 'Guest',
            }))}
            columns={[
              { key:'guest',    label:'Guest' },
              { key:'phone',    label:'Phone' },
              { key:'activity', label:'Activity' },
              { key:'checkIn',  label:'Date' },
              { key:'duration', label:'Duration' },
              { key:'paid',     label:'Paid',     render: v=>`KES ${fmtNum(v)}` },
              { key:'method',   label:'Method' },
              { key:'type',     label:'Type' },
            ]}
            onClose={()=>setModal(null)}
            onExport={exportGuestVisits}
          />
        )}

        {modal === 'top_products' && stats && (
          <ReportDetailModal
            title="Top Products"
            subtitle={`All product revenue · ${periodLabel}`}
            gradient="linear-gradient(135deg,#1e3a5f,#1d4ed8)"
            data={stats.topProducts.map((p,i) => ({ rank:i+1, name:p.name, qty:p.qty, revenue:p.revenue }))}
            columns={[
              { key:'rank',    label:'#' },
              { key:'name',    label:'Product' },
              { key:'qty',     label:'Units Sold' },
              { key:'revenue', label:'Revenue', render: v=>`KES ${fmtNum(v)}` },
            ]}
            onClose={()=>setModal(null)}
          />
        )}

        {modal === 'top_activities' && stats && (
          <ReportDetailModal
            title="Top Activities"
            subtitle={`Guest visit revenue · ${periodLabel}`}
            gradient="linear-gradient(135deg,#2e1065,#7c3aed)"
            data={stats.topActivities.map((a,i) => ({ rank:i+1, title:a.title, visits:a.visits, revenue:a.revenue }))}
            columns={[
              { key:'rank',    label:'#' },
              { key:'title',   label:'Activity' },
              { key:'visits',  label:'Visits' },
              { key:'revenue', label:'Revenue', render: v=>`KES ${fmtNum(v)}` },
            ]}
            onClose={()=>setModal(null)}
          />
        )}

      </AnimatePresence>
    </div>
  );
};

export default Reports;